import React from "react";

import { CircularProgress, Box } from "@mui/material";
import { Navigate, useLocation, Outlet } from "react-router";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { AUTH, ROOT } from "src/platform/constants/routes";

interface ProtectedRouteProps {
  children?: React.ReactNode;
  redirectPath?: string;
}

/**
 * A component that ensures a user is authenticated before allowing access to a route
 * If not authenticated, it redirects to the login page with the attempted location
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectPath = AUTH.LOGIN.path,
}) => {
  const { token, loading } = useLegacyAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!token) {
    // Redirect to login page with return URL
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // If there are children, render them, otherwise render the outlet
  return children ? <>{children}</> : <Outlet />;
};

interface PublicRouteProps {
  children?: React.ReactNode;
  restricted?: boolean;
  redirectPath?: string;
}

/**
 * A component for public routes that may optionally restrict authenticated users
 * If restricted is true and the user is authenticated, they are redirected to the home page
 * Special case: Don't redirect away from login page even if user has a token - let login page handle organization selection
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  restricted = false,
  redirectPath = ROOT.path,
}) => {
  const { token, loading } = useLegacyAuth();
  const location = useLocation();
  const from = location.state?.from || redirectPath;
  const isLoginPage = location.pathname === AUTH.LOGIN.path;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (restricted && token && !isLoginPage) {
    // Redirect to the intended location or specified redirect path
    return <Navigate to={from} replace />;
  }

  // If there are children, render them, otherwise render the outlet
  return children ? <>{children}</> : <Outlet />;
};
